import { APP_INITIALIZER, Inject, Injectable, Optional } from '@angular/core'
import { HttpClient } from '@angular/common/http'

import { Observable, of, Subject, tap } from 'rxjs'

import { TrackingService, UserLogout } from '@builder/tracking'

import { Feature, FeaturesResponse } from './features'
import { FEATURES_MIDDLEWARE, FeaturesMiddleWare } from './features-middleware'
import { environment } from '@builder/environments/environment'

// API Endpoint
const SETTINGS_ENDPOINT = 'wp-json/wp/v2/features-config'

type UserAttributes = {
  loggedIn?: boolean
  id?: number
  country?: string
  organizationId?: string | string[]
  role?: string | string[]
  myaBlogId?: number
  user_roles?: string[]
  blogId?: number
}

/**
 * Service for loading features from myAlpha
 * If a FeaturesMiddleWare is provided the setting/testing of features is off-loaded to that class
 */
@Injectable({
  providedIn: 'root'
})
export class FeaturesService {
  private features: Map<string, Feature> = new Map()
  protected userAttributes: UserAttributes = {}

  constructor(
    private http: HttpClient,
    private tracking: TrackingService,
    @Optional()
    @Inject(FEATURES_MIDDLEWARE)
    public middleware: FeaturesMiddleWare
  ) {
    this.initEvents()
  }

  /**
   * Tap into App events to possibly update the user attributes
   */
  private initEvents(): void {
    this.updateUserAttributesFromProfile()

    this.tracking.events.subscribe((event) => {
      if (event instanceof UserLogout) {
        this.updateUserAttributes({ loggedIn: false })
      }
    })
  }

  /**
   * Update User Attributes from stored Profile
   */
  private updateUserAttributesFromProfile(): void {
    try {
      const profileData = localStorage.getItem('profile')

      if (!profileData) {
        console.error('No profile data found in localStorage')
        return
      }

      const userAttributes = JSON.parse(profileData)

      if (!userAttributes) {
        console.error('Invalid profile data')
        return
      }

      this.updateUserAttributes({
        loggedIn: true,
        id: userAttributes.id,
        country: userAttributes.country || null,
        role:
          userAttributes.user_roles && userAttributes.user_roles.length > 0
            ? userAttributes.user_roles[0]
            : null,
        organizationId: userAttributes.organizationId || null,
        myaBlogId: userAttributes.blogId || null
      })
    } catch (error) {
      console.error('Error parsing profile data from localStorage:', error)
    }
  }

  /**
   * Load the Settings
   */
  public load(): Observable<any> {
    if ((environment as Record<string, any>).localEnvironment) {
      return this.loadFromLocalGrowthbook()
    } else {
      return this.loadFromGrowthbook()
    }
  }

  /**
   * Load the Settings
   */
  public loadFromLocalGrowthbook(): Observable<any> {
    return of({
      status: 200,
      features: {
        sso_enabled: {
          defaultValue: true
        },
        cora: {
          defaultValue: true
        },
        'user-profile': {
          defaultValue: true
        },
        'product-tour-overview': {
          defaultValue: true
        },
        'guest-app-onboarding': {
          defaultValue: true
        },
        'search-update': {
          defaultValue: true
        },
        'new-video-player-modal': {
          defaultValue: true
        },
        'ays-launch': {
          defaultValue: true
        },
        cinderella: {
          defaultValue: true
        },
        'mui-134': {
          defaultValue: true
        },
        'schedule-move-it': {
          defaultValue: true
        }
      },
      dateUpdated: '2025-02-26T15:16:46.892Z'
    }).pipe(
      tap((result) => {
        const featuresObj = Object.entries(
          result.features as Record<string, any>
        ).reduce((acc, curr) => {
          const [key, val] = curr
          acc[key] = {
            key,
            enabled: val.defaultValue,
            data: null,
            rules: []
          }
          return acc
        }, {})
        this.onLoadFeatures(featuresObj as FeaturesResponse)
      })
    )
  }

  /**
   * Load the Settings
   */
  public loadFromGrowthbook(): Observable<any> {
    return this.http
      .get<FeaturesResponse>(SETTINGS_ENDPOINT)
      .pipe(tap((result) => this.onLoadFeatures(result)))
  }

  private onLoadFeatures(result: FeaturesResponse) {
    if (this.middleware) {
      this.middleware.setFeatures(result)
      return
    }

    Object.values(result).forEach((feature: Feature) =>
      this.setFeature(feature.key, feature)
    )
  }

  /**
   * Set a feature and create a Subject for it's observation
   */
  private setFeature(key: string, feature: Feature) {
    if (this.middleware) {
      this.middleware.setFeature(key, feature)
      return
    }
    this.features.set(key, feature)
  }

  /**
   * Check if a feature is enabled
   */
  public isOn(key: string): boolean {
    if (this.middleware) {
      return this.middleware.isOn(key)
    }

    const feature = this.features.get(key)
    return feature && feature.enabled
  }

  /**
   * Check if a feature is disabled
   */
  public isOff(key: string): boolean {
    if (this.middleware) {
      return this.middleware.isOff(key)
    }

    const feature = this.features.get(key)
    return !feature || !feature.enabled
  }

  /**
   * whenChanged for when status of a flag changes
   */
  public whenChanged(key: string): Subject<Feature> {
    if (this.middleware) {
      return this.middleware.whenChanged(key)
    }
    console.warn(
      'whenChanged is not supported for default feature implementation'
    )

    // return a subject that will never notify
    return new Subject()
  }

  /**
   * Get features
   */
  public getFeatures() {
    if (this.middleware) {
      return this.middleware.getFeatures()
    }
    return this.features
  }

  /**
   * Update user attributes
   */
  public updateUserAttributes(val: UserAttributes): void {
    if (this.middleware) {
      this.userAttributes = { ...this.userAttributes, ...val }
      return this.middleware.updateUserAttributes(this.userAttributes)
    }
    console.warn(
      'User Attributes not supported for default feature implementation'
    )
  }
}

/**
 * Provider for import into app module
 */
export const featuresProvider = {
  provide: APP_INITIALIZER,
  deps: [FeaturesService],
  useFactory: (service: FeaturesService) => () => service.load(),
  multi: true
}
